import { useState } from 'react';

interface Props {
  userId: string | null;
  disabled?: boolean;
}

interface ConnectConfig {
  connectId: string;
  name: string;
  urlToPublishTo: string;
}

export default function SetupConnect({ userId, disabled }: Props) {
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState<ConnectConfig[]>([]);
  const [result, setResult] = useState<{ success: boolean; connectId?: string; error?: string } | null>(null);

  const handleSetup = async () => {
    if (!userId) return;

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch('/api/connect/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();

      if (response.ok) {
        setResult({ success: true, connectId: data.connectId });
        loadConfigs();
      } else {
        setResult({ success: false, error: data.error });
      }
    } catch (err) {
      setResult({ success: false, error: 'Failed to setup webhook' });
    } finally {
      setLoading(false);
    }
  };

  const loadConfigs = async () => {
    if (!userId) return;

    try {
      const response = await fetch(`/api/connect/list?userId=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setConfigs(data.configurations || []);
      }
    } catch (err) {
      console.error('Failed to load configs:', err);
    }
  };

  if (disabled) {
    return (
      <div className="text-gray-400">
        <p className="text-sm">Connect DocuSign first to setup webhooks.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button
        onClick={handleSetup}
        disabled={loading}
        className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
      >
        {loading ? 'Setting up...' : 'Setup Webhook'}
      </button>

      {result && (
        <div
          className={`p-3 rounded-md text-sm ${
            result.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {result.success ? (
            <p>Webhook configured! ID: {result.connectId}</p>
          ) : (
            <p>Error: {result.error}</p>
          )}
        </div>
      )}

      <button
        onClick={loadConfigs}
        className="text-sm text-indigo-600 hover:text-indigo-800"
      >
        View existing configurations
      </button>

      {configs.length > 0 && (
        <div className="mt-2 space-y-2">
          {configs.map((config) => (
            <div key={config.connectId} className="text-xs bg-gray-50 p-2 rounded">
              <p className="font-medium">{config.name}</p>
              <p className="text-gray-500 truncate">{config.urlToPublishTo}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
